import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, MessageCircle, BadgeCheck, ExternalLink } from "lucide-react";
import { PEOPLE, getVerifiedSolutions } from "@/data/mockPeople";
import ProblemListItem from "@/components/ProblemListItem";
import ContactSheet from "@/components/ContactSheet";
import InterestedFeed from "@/components/InterestedFeed";
import FollowButton from "@/components/FollowButton";
import { api } from "@/contexts/AuthContext";
import { realUserToPerson } from "@/lib/adapters";

// Strip protocol + trailing slash to show a clean domain when the user hasn't
// supplied a custom label for a link.
function prettyHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  }
}

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const [realPerson, setRealPerson] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const mockPerson = PEOPLE.find((p) => p.id === id);

  useEffect(() => {
    if (mockPerson) return;
    setRealPerson(null);
    setNotFound(false);
    api
      .get(`/users/${id}`)
      .then((r) => setRealPerson(realUserToPerson(r.data)))
      .catch(() => setNotFound(true));
  }, [id, mockPerson]);

  const person = mockPerson || realPerson;

  if (!person && notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-[#111827]">
          Profile not found
        </h1>
        <p className="mt-3 text-[#4B5563]">
          The person you're looking for isn't in our directory.
        </p>
        <button
          onClick={() => navigate("/")}
          data-testid="back-to-directory"
          className="mt-8 px-5 py-2.5 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-medium transition-colors"
        >
          Back to directory
        </button>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#9CA3AF]">
        Loading profile…
      </div>
    );
  }

  const verified = getVerifiedSolutions(person);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-16">
      <Link
        to="/"
        data-testid="back-link"
        className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#111827] transition-colors"
      >
        <ArrowLeft size={16} />
        Back to directory
      </Link>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        {/* Left: identity + metrics + tools + contact */}
        <aside className="md:col-span-4 md:sticky md:top-24 md:self-start">
          <div className="bg-white rounded-3xl p-8 ring-1 ring-[#F3F4F6]">
            <img
              src={person.photo}
              alt={person.name}
              className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-sm"
            />
            <h1
              data-testid="profile-name"
              className="mt-6 font-display font-bold text-3xl leading-tight text-[#111827]"
            >
              {person.name}
            </h1>
            <p className="mt-1 text-[#4B5563]">{person.role}</p>

            <FollowButton personId={person.id} isDemo={person.isDemo} />

            {/* Reputation metrics */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div
                data-testid="metric-verified"
                className="rounded-2xl bg-[#ECFDF5] ring-1 ring-[#D1FAE5] p-4"
              >
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#065F46]">
                  <BadgeCheck size={12} />
                  Solutions
                </p>
                <p className="mt-1 font-display font-bold text-2xl text-[#065F46]">
                  {verified}
                </p>
                <p className="text-xs text-[#065F46]/80 mt-0.5">
                  {verified === 1 ? "Listed" : "Listed"}
                </p>
              </div>
              <div
                data-testid="metric-posted"
                className="rounded-2xl bg-[#FAFAFA] ring-1 ring-[#F3F4F6] p-4"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
                  Problems
                </p>
                <p className="mt-1 font-display font-bold text-2xl text-[#111827]">
                  {person.problemsPosted}
                </p>
                <p className="text-xs text-[#4B5563] mt-0.5">Posted</p>
              </div>
            </div>

            <p className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#9CA3AF]">
              <MapPin size={14} />
              {person.location}
            </p>

            <p className="mt-4 text-[#4B5563] leading-relaxed">{person.bio}</p>

            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-3">
                Categories
              </p>
              <div className="flex flex-wrap gap-1.5">
                {person.categories.map((c) => (
                  <Link
                    key={c}
                    to={`/categories/${encodeURIComponent(c)}`}
                    data-testid={`profile-category-${c}`}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563] hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
                  >
                    {c}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-3">
                AI Products they use
              </p>
              <div className="flex flex-wrap gap-1.5">
                {person.tools.map((t) => (
                  <Link
                    key={t.name}
                    to={`/tools/${encodeURIComponent(t.name)}`}
                    data-testid={`profile-tool-${t.name}`}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
                  >
                    {t.name}
                  </Link>
                ))}
              </div>
            </div>

            {(person.links && person.links.length > 0) && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-3">
                  Links
                </p>
                <ul className="space-y-2">
                  {person.links.map((l, i) => (
                    <li key={i}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`profile-link-${i}`}
                        className="inline-flex items-center gap-2 text-sm text-[#111827] hover:text-[#3FFFB6] transition-colors group"
                      >
                        <ExternalLink size={14} className="text-[#9CA3AF] group-hover:text-[#3FFFB6]" />
                        <span className="font-medium underline-offset-4 group-hover:underline">
                          {l.label || prettyHost(l.url)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={() => setContactOpen(true)}
              data-testid="open-contact"
              disabled={person.isDemo}
              className="mt-8 w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF] disabled:cursor-not-allowed text-[#111827] font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <MessageCircle size={16} />
              {person.isDemo ? "Demo profile" : "Request help"}
            </button>
          </div>
        </aside>

        {/* Right: Problems solved (centrepiece) */}
        <section className="md:col-span-8">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display font-bold text-3xl text-[#111827]">
              Problems solved
            </h2>
            <span className="text-sm text-[#9CA3AF]">
              {verified} {verified === 1 ? "solution" : "solutions"} listed
            </span>
          </div>
          <p className="mt-2 text-[#4B5563] max-w-xl">
            Real AI problems {person.name.split(" ")[0]} has solved, with the
            tools, categories, and outcomes attached.
          </p>

          <div className="mt-8 bg-white rounded-3xl ring-1 ring-[#F3F4F6] px-8">
            {person.problems.map((problem, i) => (
              <ProblemListItem
                key={i}
                problem={problem}
                index={i}
                person={person}
              />
            ))}
          </div>

          <InterestedFeed person={person} />
        </section>
      </div>

      <ContactSheet
        open={contactOpen}
        onOpenChange={setContactOpen}
        person={person}
      />
    </div>
  );
}
